import React, { useEffect, useState, useCallback } from 'react';
import { getAnalyticsApiBaseUrl } from '../config/api';
import { fetchWithAuth } from '../utils/auth';

const MODES = ['all', 'food', 'medicine', 'plant', 'animal', 'product', 'document', 'repair', 'workout', 'hygiene', 'pest', 'auto'];
const MODE_ICONS: Record<string, string> = {
  food: '🍔', medicine: '💊', plant: '🌿', animal: '🐾', product: '📦',
  document: '📄', repair: '🔧', workout: '💪', hygiene: '🧴', pest: '🐛', auto: '🔍',
};
const MODE_COLORS: Record<string, string> = {
  food: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
  medicine: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  plant: 'bg-green-500/20 text-green-300 border-green-500/30',
  animal: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  product: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  document: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
  repair: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
  workout: 'bg-red-500/20 text-red-300 border-red-500/30',
  hygiene: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
  pest: 'bg-lime-500/20 text-lime-300 border-lime-500/30',
  auto: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30',
};

interface Scan {
  scanId: string;
  userId: string;
  mode: string;
  status: string;
  itemName: string;
  keyStat: string;
  confidence: number;
  language: string;
  scannedAt: string;
  shared: boolean;
  thumbnailUrl?: string;
  imageUrl?: string;
  imageUrls?: string[];
  resultJson?: any;
  aiTrace?: {
    selectedProvider?: string;
    selectedModel?: string;
    totalEstimatedCostUsd?: number;
    attempts?: Array<{
      provider: string;
      model: string;
      success: boolean;
      latencyMs: number;
      promptTokens?: number;
      completionTokens?: number;
      totalTokens?: number;
      estimatedCostUsd?: number;
      error?: string;
    }>;
  };
}

const ScansBrowser: React.FC = () => {
  const [scans, setScans] = useState<Scan[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('all');
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetchScans = useCallback(async () => {
    setLoading(true);
    try {
      const base = getAnalyticsApiBaseUrl();
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (mode !== 'all') params.set('mode', mode);
      if (status !== 'all') params.set('status', status);
      if (search) params.set('search', search);
      const res = await fetchWithAuth(`${base}/analytics/lens-scans?${params}`);
      const data = await res.json();
      setScans(data.scans || []);
      setTotal(data.total || 0);
      setPages(data.pages || 0);
    } catch (e) {
      console.error('Failed to fetch scans:', e);
    } finally {
      setLoading(false);
    }
  }, [page, mode, status, search]);

  useEffect(() => { fetchScans(); }, [fetchScans]);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleModeChange = (m: string) => {
    setMode(m);
    setPage(1);
    setExpandedId(null);
  };

  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  const confidenceColor = (c: number) => {
    if (c >= 0.8) return 'text-green-400';
    if (c >= 0.5) return 'text-yellow-400';
    return 'text-red-400';
  };

  const renderResultDetails = (scan: Scan) => {
    const r = scan.resultJson || {};
    const trace = scan.aiTrace;

    return (
      <div className="mt-4 space-y-4 border-t border-white/10 pt-4">
        {/* Images */}
        {scan.imageUrls && scan.imageUrls.length > 0 && (
          <div>
            <h4 className="text-xs font-medium text-gray-400 uppercase mb-2">Images ({scan.imageUrls.length})</h4>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {scan.imageUrls.map((url, i) => (
                <a key={i} href={url} target="_blank" rel="noreferrer">
                  <img
                    src={url}
                    alt={`Scan ${i + 1}`}
                    className="h-32 w-32 object-cover rounded-lg border border-white/10 hover:border-[#FF9C15]/50 transition-all flex-shrink-0"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* AI Trace */}
        {trace && (
          <div>
            <h4 className="text-xs font-medium text-gray-400 uppercase mb-2">AI Trace</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="bg-white/5 rounded-lg p-2">
                <p className="text-[10px] text-gray-500">Provider</p>
                <p className="text-xs text-white font-mono">{trace.selectedProvider || '—'}</p>
              </div>
              <div className="bg-white/5 rounded-lg p-2">
                <p className="text-[10px] text-gray-500">Model</p>
                <p className="text-xs text-white font-mono truncate">{trace.selectedModel || '—'}</p>
              </div>
              <div className="bg-white/5 rounded-lg p-2">
                <p className="text-[10px] text-gray-500">Cost</p>
                <p className="text-xs text-white font-mono">{trace.totalEstimatedCostUsd ? `$${trace.totalEstimatedCostUsd.toFixed(4)}` : '—'}</p>
              </div>
              <div className="bg-white/5 rounded-lg p-2">
                <p className="text-[10px] text-gray-500">Attempts</p>
                <p className="text-xs text-white font-mono">{trace.attempts?.length || 0}</p>
              </div>
            </div>
            {trace.attempts && trace.attempts.length > 0 && (
              <div className="mt-2 space-y-1">
                {trace.attempts.map((a, i) => (
                  <div key={i} className={`flex items-center gap-2 text-xs px-2 py-1 rounded ${a.success ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                    <span className={a.success ? 'text-green-400' : 'text-red-400'}>{a.success ? '✓' : '✗'}</span>
                    <span className="text-gray-300 font-mono">{a.provider}/{a.model}</span>
                    <span className="text-gray-500">{a.latencyMs}ms</span>
                    {a.totalTokens && <span className="text-gray-500">{a.totalTokens} tok</span>}
                    {a.estimatedCostUsd && <span className="text-gray-500">${a.estimatedCostUsd.toFixed(4)}</span>}
                    {a.error && <span className="text-red-400 truncate">{a.error}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Result JSON - mode-specific rendering */}
        <div>
          <h4 className="text-xs font-medium text-gray-400 uppercase mb-2">Result Details</h4>
          {scan.mode === 'food' && r.detected_mode === 'food' ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[
                  { label: 'Dish', value: r.dish_name || r.itemName },
                  { label: 'Cuisine', value: r.cuisine },
                  { label: 'Calories', value: r.nutrition?.calories ? `${r.nutrition.calories} kcal` : null },
                  { label: 'Health Score', value: r.health_score != null ? `${r.health_score}/10` : null },
                ].filter(x => x.value).map((item, i) => (
                  <div key={i} className="bg-white/5 rounded-lg p-2">
                    <p className="text-[10px] text-gray-500">{item.label}</p>
                    <p className="text-xs text-white">{item.value}</p>
                  </div>
                ))}
              </div>
              {r.nutrition && (
                <div className="flex flex-wrap gap-2">
                  {Object.entries(r.nutrition).filter(([, v]) => v != null).map(([k, v]) => (
                    <span key={k} className="text-[10px] bg-white/5 rounded px-2 py-0.5 text-gray-300">
                      {k}: {String(v)}
                    </span>
                  ))}
                </div>
              )}
              {r.allergens && r.allergens.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {r.allergens.map((a: string, i: number) => (
                    <span key={i} className="text-[10px] bg-red-500/20 text-red-300 rounded px-2 py-0.5">{a}</span>
                  ))}
                </div>
              )}
            </div>
          ) : scan.mode === 'medicine' && r.detected_mode === 'medicine' ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[
                  { label: 'Brand', value: r.brand_name },
                  { label: 'Generic', value: r.generic_name },
                  { label: 'Drug Class', value: r.drug_class_simple || r.drug_class },
                  { label: 'Form', value: r.form },
                  { label: 'Strength', value: r.strength_dosage },
                  { label: 'Manufacturer', value: r.manufacturer },
                  { label: 'Expiry', value: r.expiry_date },
                  { label: 'Prescription', value: r.requires_prescription ? 'Yes' : 'No' },
                ].filter(x => x.value).map((item, i) => (
                  <div key={i} className="bg-white/5 rounded-lg p-2">
                    <p className="text-[10px] text-gray-500">{item.label}</p>
                    <p className="text-xs text-white">{item.value}</p>
                  </div>
                ))}
              </div>
              {r.safety_verdict && (
                <div className={`rounded-lg p-2 text-xs ${
                  r.safety_verdict.colour === 'green' ? 'bg-green-500/10 text-green-300' :
                  r.safety_verdict.colour === 'amber' ? 'bg-yellow-500/10 text-yellow-300' :
                  'bg-red-500/10 text-red-300'
                }`}>
                  Safety: {r.safety_verdict.summary}
                </div>
              )}
            </div>
          ) : scan.mode === 'product' && r.detected_mode === 'product' ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {[
                  { label: 'Product', value: r.identity?.product_name || r.product_name },
                  { label: 'Brand', value: r.identity?.brand || r.brand },
                  { label: 'Category', value: r.identity?.category || r.category },
                  { label: 'Verdict', value: r.ai_verdict?.verdict_label_local || r.verdict?.recommendation },
                  { label: 'Score', value: r.ai_verdict?.overall_score != null ? `${r.ai_verdict.overall_score}/100` : null },
                  { label: 'Price Range', value: r.price_intelligence?.fair_price_range_local },
                ].filter(x => x.value).map((item, i) => (
                  <div key={i} className="bg-white/5 rounded-lg p-2">
                    <p className="text-[10px] text-gray-500">{item.label}</p>
                    <p className="text-xs text-white">{item.value}</p>
                  </div>
                ))}
              </div>
              {r.ai_verdict?.ai_recommendation_note && (
                <p className="text-xs text-gray-400 bg-white/5 rounded-lg p-2">{r.ai_verdict.ai_recommendation_note}</p>
              )}
            </div>
          ) : (
            // Generic fallback for plant, animal, document, etc.
            <div className="space-y-2">
              {r.opening_chat_message && (
                <p className="text-xs text-gray-300 bg-white/5 rounded-lg p-3 italic">"{r.opening_chat_message}"</p>
              )}
              {r.summary && (
                <p className="text-xs text-gray-400 bg-white/5 rounded-lg p-2">{r.summary}</p>
              )}
              {r.description && !r.summary && (
                <p className="text-xs text-gray-400 bg-white/5 rounded-lg p-2">{r.description}</p>
              )}
              {/* Show key fields */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {Object.entries(r)
                  .filter(([k, v]) => typeof v === 'string' && v.length < 100 && !['detected_mode', 'opening_chat_message', 'summary', 'description', 'language', 'country', 'disclaimer'].includes(k))
                  .slice(0, 9)
                  .map(([k, v]) => (
                    <div key={k} className="bg-white/5 rounded-lg p-2">
                      <p className="text-[10px] text-gray-500">{k.replace(/_/g, ' ')}</p>
                      <p className="text-xs text-white truncate">{String(v)}</p>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>

        {/* Meta */}
        <div className="flex flex-wrap gap-2 text-[10px] text-gray-500">
          <span>Scan ID: {scan.scanId}</span>
          <span>|</span>
          <span>User: {scan.userId}</span>
          <span>|</span>
          <span>Lang: {scan.language}</span>
          {scan.shared && <><span>|</span><span className="text-blue-400">Shared</span></>}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-4">
        <div className="flex flex-col md:flex-row gap-3">
          {/* Search */}
          <div className="flex-1 flex gap-2">
            <input
              type="text"
              placeholder="Search by item name, scan ID, or user ID..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#FF9C15]/50"
            />
            <button onClick={handleSearch} className="px-4 py-2 bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white rounded-lg text-sm font-medium hover:opacity-90">
              Search
            </button>
          </div>
          {/* Status filter */}
          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none"
          >
            <option value="all">All Status</option>
            <option value="success">Success</option>
            <option value="error">Error</option>
            <option value="processing">Processing</option>
          </select>
        </div>
        {/* Mode pills */}
        <div className="flex flex-wrap gap-1.5 mt-3">
          {MODES.map((m) => (
            <button
              key={m}
              onClick={() => handleModeChange(m)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-all border ${
                mode === m
                  ? 'bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white border-transparent'
                  : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10'
              }`}
            >
              {m !== 'all' && MODE_ICONS[m]} {m === 'all' ? 'All Modes' : m.charAt(0).toUpperCase() + m.slice(1)}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-2">{total.toLocaleString()} scans found</p>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-8">
          <div className="w-8 h-8 border-3 border-[#FF9C15] border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Scan Cards */}
      {!loading && (
        <div className="space-y-2">
          {scans.map((scan) => {
            const isExpanded = expandedId === scan.scanId;
            const modeColor = MODE_COLORS[scan.mode] || 'bg-gray-500/20 text-gray-300 border-gray-500/30';
            return (
              <div
                key={scan.scanId}
                className={`bg-white/5 backdrop-blur-sm border rounded-xl overflow-hidden transition-all duration-200 ${
                  isExpanded ? 'border-[#FF9C15]/40' : 'border-white/10 hover:border-white/20'
                }`}
              >
                {/* Collapsed card */}
                <button
                  onClick={() => setExpandedId(isExpanded ? null : scan.scanId)}
                  className="w-full flex items-center gap-3 p-3 text-left"
                >
                  {/* Thumbnail */}
                  <div className="w-14 h-14 rounded-lg bg-white/5 border border-white/10 flex-shrink-0 overflow-hidden">
                    {scan.thumbnailUrl ? (
                      <img
                        src={scan.thumbnailUrl}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                          (e.target as HTMLImageElement).parentElement!.innerHTML = `<span class="flex items-center justify-center w-full h-full text-lg">${MODE_ICONS[scan.mode] || '📷'}</span>`;
                        }}
                      />
                    ) : (
                      <span className="flex items-center justify-center w-full h-full text-lg">{MODE_ICONS[scan.mode] || '📷'}</span>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${modeColor}`}>
                        {scan.mode}
                      </span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                        scan.status === 'success' ? 'bg-green-500/20 text-green-400' :
                        scan.status === 'error' ? 'bg-red-500/20 text-red-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>{scan.status}</span>
                      {scan.confidence > 0 && (
                        <span className={`text-[10px] font-mono ${confidenceColor(scan.confidence)}`}>
                          {(scan.confidence * 100).toFixed(0)}%
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-white font-medium truncate">{scan.itemName || 'Unknown'}</p>
                    <p className="text-xs text-gray-500 truncate">{scan.keyStat || '—'}</p>
                  </div>

                  {/* Right side */}
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className="text-[10px] text-gray-500">{timeAgo(scan.scannedAt)}</span>
                    {scan.aiTrace?.totalEstimatedCostUsd && (
                      <span className="text-[10px] text-gray-500 font-mono">${scan.aiTrace.totalEstimatedCostUsd.toFixed(4)}</span>
                    )}
                    <svg className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="px-3 pb-4">
                    {renderResultDetails(scan)}
                  </div>
                )}
              </div>
            );
          })}

          {scans.length === 0 && !loading && (
            <div className="text-center py-12 text-gray-500">No scans found</div>
          )}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 text-sm bg-white/5 border border-white/10 rounded-lg text-gray-400 hover:bg-white/10 disabled:opacity-30"
          >
            Prev
          </button>
          <span className="text-sm text-gray-400">
            Page {page} of {pages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(pages, p + 1))}
            disabled={page === pages}
            className="px-3 py-1.5 text-sm bg-white/5 border border-white/10 rounded-lg text-gray-400 hover:bg-white/10 disabled:opacity-30"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default ScansBrowser;
